import React from 'react'
import { Box, Button, HStack, Tag, TagLabel, Text, Wrap, WrapItem, Badge, IconButton } from '@chakra-ui/react'
import { FiX } from 'react-icons/fi'
import { useAppTranslation } from '@/contexts/LanguageContext'
import { endpointId, type GraphLinkDatum, type GraphNodeDatum } from './graphTransforms'
import { mimeGlyph, resolveNodeColor } from './nodeStyle'

export interface LinkGraphNodePanelProps {
  node: GraphNodeDatum
  centerId: number
  /** Explicit link joining this node and the center, if any. */
  explicitLink?: GraphLinkDatum
  /** Suggestion edge joining this node and the center, if any. */
  relatedLink?: GraphLinkDatum
  isExpanding: boolean
  isLinking: boolean
  isRemoving: boolean
  onOpen: () => void
  onExpand: () => void
  onLink: () => void
  onRemoveLink: () => void
  onClose: () => void
}

export default function LinkGraphNodePanel({
  node,
  centerId,
  explicitLink,
  relatedLink,
  isExpanding,
  isLinking,
  isRemoving,
  onOpen,
  onExpand,
  onLink,
  onRemoveLink,
  onClose,
}: LinkGraphNodePanelProps) {
  const { t } = useAppTranslation('files')
  const isCenter = node.id === centerId
  const busy = isExpanding || isLinking || isRemoving

  let relationLabel: string | null = null
  if (explicitLink?.relation_type) {
    const outgoingFromCenter = endpointId(explicitLink.source) === centerId
    relationLabel = outgoingFromCenter
      ? t(`filePreview.linkTypes.${explicitLink.relation_type}`)
      : t(`filePreview.linkTypes.inverse.${explicitLink.relation_type}`)
  } else if (relatedLink) {
    relationLabel = t('filePreview.linkGraph.relatedScore', {
      score: `${Math.round((relatedLink.score ?? 0) * 100)}%`,
    })
  }

  return (
    <Box
      position="absolute"
      left={3}
      bottom={3}
      zIndex={5}
      w="280px"
      maxW="calc(100% - 24px)"
      bg="white"
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="lg"
      boxShadow="lg"
      p={3}
      textAlign="left"
    >
      <HStack align="flex-start" spacing={2} mb={2}>
        <Box
          flexShrink={0}
          w="28px"
          h="28px"
          borderRadius="full"
          bg={resolveNodeColor(node)}
          color="white"
          fontSize="9px"
          fontWeight="700"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          {mimeGlyph(node.mime_type)}
        </Box>
        <Box flex={1} minW={0}>
          <Text fontSize="sm" fontWeight="600" noOfLines={2} lineHeight="short">
            {node.name}
          </Text>
          {isCenter ? (
            <Badge colorScheme="brand" variant="subtle" fontSize="0.6rem" mt={1}>
              {t('filePreview.linkGraph.currentDocument')}
            </Badge>
          ) : (
            relationLabel && (
              <Badge
                colorScheme={explicitLink ? 'brand' : 'gray'}
                variant={explicitLink ? 'subtle' : 'outline'}
                fontSize="0.6rem"
                mt={1}
              >
                {relationLabel}
              </Badge>
            )
          )}
        </Box>
        <IconButton aria-label="Close" icon={<FiX />} size="xs" variant="ghost" onClick={onClose} />
      </HStack>

      {explicitLink?.note && (
        <Text fontSize="xs" color="gray.600" noOfLines={2} mb={2}>
          {explicitLink.note}
        </Text>
      )}

      {node.tags.length > 0 && (
        <Wrap spacing={1} mb={2}>
          {node.tags.slice(0, 4).map((tag) => (
            <WrapItem key={tag.tag}>
              <Tag size="sm" variant="subtle" colorScheme="gray">
                <TagLabel fontSize="0.65rem">{tag.tag}</TagLabel>
              </Tag>
            </WrapItem>
          ))}
        </Wrap>
      )}

      <HStack spacing={2} flexWrap="wrap">
        {!isCenter && (
          <Button size="xs" colorScheme="brand" variant="solid" onClick={onOpen} isDisabled={busy}>
            {t('filePreview.linkGraph.open')}
          </Button>
        )}
        {!isCenter && !node.expanded && (
          <Button size="xs" variant="outline" onClick={onExpand} isLoading={isExpanding} isDisabled={busy}>
            {t('filePreview.linkGraph.expand')}
          </Button>
        )}
        {!isCenter && node.expanded && (
          <Text fontSize="xs" color="gray.500">
            {t('filePreview.linkGraph.expanded')}
          </Text>
        )}
        {!isCenter && relatedLink && !explicitLink && (
          <Button size="xs" variant="outline" colorScheme="brand" onClick={onLink} isLoading={isLinking} isDisabled={busy}>
            {t('filePreview.linkGraph.link')}
          </Button>
        )}
        {!isCenter && explicitLink && (
          <Button size="xs" variant="ghost" colorScheme="red" onClick={onRemoveLink} isLoading={isRemoving} isDisabled={busy}>
            {t('filePreview.linkGraph.removeLink')}
          </Button>
        )}
      </HStack>
    </Box>
  )
}
